import { getBlogPost, getBlogPosts, getImageUrl } from '../../services/blogService';
import { useEffect, useState } from 'react';

import type { BlogPost } from '../../types/blog';
import type { Components } from 'react-markdown';
import LandingFooter from '../ui/LandingFooter';
import LandingHeader from '../ui/LandingHeader';
import { Link } from '@tanstack/react-router';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { router } from '../../router';
import { useParams } from '@tanstack/react-router';
import { SEO } from '../SEO';

export default function BlogPostPage() {
  const { slug } = useParams({
    from: '/blog/$slug'
  });
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPost() {
      if (!slug) return;

      setLoading(true);
      const currentPost = await getBlogPost(slug);
      if (currentPost) {
        setPost(currentPost);

        // Preload all images in the content
        const imageRegex = /!\[.*?\]\((.*?)\)/g;
        const matches = Array.from(currentPost.content.matchAll(imageRegex));
        const imagePaths = matches.map(match => (match as RegExpMatchArray)[1]);

        // Load all images in parallel
        const imagePromises = imagePaths.map(async (imagePath) => {
          try {
            // Extract just the filename from the path
            const imageName = imagePath.split('/').pop() || imagePath;
            const url = await getImageUrl(slug, imageName);
            setImageUrls(prev => ({ ...prev, [imagePath]: url }));
          } catch (error) {
            console.error(`Error loading image ${imagePath}:`, error);
            setImageUrls(prev => ({ ...prev, [imagePath]: '' }));
          }
        });

        await Promise.all(imagePromises);

        // Fetch related posts
        const allPosts = await getBlogPosts();
        const related = allPosts
          .filter(p => p.slug !== slug) // Exclude current post
          .filter(p =>
            p.category === currentPost.category || // Same category
            p.tags.some((tag: string) => currentPost.tags.includes(tag)) // Shared tags
          )
          .sort((a, b) => {
            // Sort by number of matching tags
            const aTagMatches = a.tags.filter((tag: string) => currentPost.tags.includes(tag)).length;
            const bTagMatches = b.tags.filter((tag: string) => currentPost.tags.includes(tag)).length;
            return bTagMatches - aTagMatches;
          })
          .slice(0, 2); // Get top 2 related posts

        setRelatedPosts(related);
      }
      setLoading(false);
    }

    loadPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="h-64 bg-gray-200 rounded-lg mb-8"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <SEO 
          title="Post Not Found"
          description="The requested blog post could not be found."
        />
        <p>Post not found</p>
        <Link to="/blog" className="text-gray-900 hover:underline">
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title={post.title}
        description={post.description}
        keywords={post.tags.join(', ')}
        ogImage={post.image}
        ogUrl={`https://preswald.com/blog/${post.slug}`}
        canonical={`https://preswald.com/blog/${post.slug}`}
      />
      <LandingHeader />
      <div className="max-w-4xl mx-auto px-4 py-8 flex-1">
        {/* Back to Blog Button */}
        <div className="mb-8">
          <Link to="/blog" className="text-gray-900 hover:underline flex items-center">
            <span className="mr-2">←</span> Back to Blog
          </Link>
        </div>

        <article>
          {/* Featured Image */}
          <div className="mb-8 overflow-hidden rounded-lg">
            {post.image ? (
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-[240px] object-cover"
                loading="eager"
              />
            ) : (
              <div className="w-full h-[240px] bg-gray-100 flex items-center justify-center">
                <span className="text-gray-400">No cover image available</span>
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-4xl font-bold mb-8 text-gray-900">{post.title.replace(/['"`]/g, '')}</h1>

          {/* Meta Information */}
          <div className="flex items-center gap-4 mb-6">
            {post.avatar && (
              <img
                src={post.avatar}
                alt={post.author}
                className="w-6 h-6 rounded-full"
                loading="eager"
              />
            )}
            <div className="flex items-center gap-2 text-gray-600">
              <span className="font-medium text-gray-900">{post.author}</span>
              <span>•</span>
              <time>
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </time>
              <span>•</span>
              <span>{post.readingTime}</span>
            </div>
          </div>

          {/* Category Badge */}
          <div className="mb-6">
            <span className="text-gray-600">Category: </span>
            <Link
              to={`/blog?category=${post.category.toLowerCase()}`}
              className="px-3 py-1 bg-gray-100 text-gray-900 rounded-full text-sm hover:bg-gray-200"
            >
              {post.category}
            </Link>
          </div>

          <hr className="my-8" />

          {/* Markdown Content */}
          <div className="prose prose-lg max-w-none prose-headings:font-bold prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-h4:text-xl prose-p:text-gray-700 prose-a:text-blue-600 hover:prose-a:text-blue-800 prose-strong:text-gray-900 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-gray-900 prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:p-4 prose-pre:overflow-x-auto prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600 prose-ul:list-disc prose-ol:list-decimal prose-li:marker:text-gray-500">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                img: ({ src, alt }) => {
                  if (!src || !post.slug) return null;

                  if (!imageUrls[src]) {
                    return (
                      <div className="animate-pulse bg-gray-200 h-48 w-full rounded-lg" />
                    );
                  }

                  if (imageUrls[src] === '') {
                    return (
                      <div className="h-48 w-full bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-gray-400">Image not available</span>
                      </div>
                    );
                  }

                  return (
                    <img
                      src={imageUrls[src]}
                      alt={alt || ''}
                      className="rounded-lg my-4"
                      loading="lazy"
                    />
                  );
                },
                code: ({ className, children, ...props }) => {
                  const match = /language-(\w+)/.exec(className || '');
                  return match ? (
                    <div className="bg-gray-100 rounded-lg p-4 my-4 overflow-x-auto">
                      <SyntaxHighlighter
                        style={{ ...oneLight }}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    </div>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
                h1: ({ children }) => (
                  <h1 className="text-4xl font-bold mt-8 mb-4 text-gray-900">{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-3xl font-bold mt-8 mb-4 text-gray-900">{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-2xl font-bold mt-6 mb-3 text-gray-900">{children}</h3>
                ),
                h4: ({ children }) => (
                  <h4 className="text-xl font-bold mt-4 mb-2 text-gray-900">{children}</h4>
                ),
                p: ({ children }) => (
                  <p className="text-gray-700 leading-relaxed mb-4">{children}</p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="text-gray-700">{children}</li>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4">
                    {children}
                  </blockquote>
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-blue-600 hover:text-blue-800 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-4">
                    <table className="min-w-full border-collapse">
                      {children}
                    </table>
                  </div>
                ),
                th: ({ children }) => (
                  <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-gray-900">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="border border-gray-300 px-4 py-2 text-gray-700">
                    {children}
                  </td>
                ),
              } as Components}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/blog?tag=${tag.toLowerCase()}`}
                  className="px-3 py-1 bg-gray-100 text-gray-900 rounded-full text-sm hover:bg-gray-200"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-12">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Related Posts</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                    />
                    <div className="p-4">
                      <h2 className="text-lg font-semibold mb-2 line-clamp-2 text-gray-900">
                        {related.title}
                      </h2>
                      <p className="text-sm text-gray-600">
                        By <span className="font-medium text-gray-900">{related.author}</span> •{' '}
                        {new Date(related.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
      <LandingFooter />
    </div>
  );
} 